import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Search, UserPlus, UserMinus, Check, X, Users, Trophy, Sparkles, Eye } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FriendsList = ({ onSelectFriend, isDark }) => {
  const { token } = useAuth();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchFriends();
    fetchRequests();
    fetchRecommendations();
  }, []);

  // Live search with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      liveSearch(searchQuery.trim());
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/friends/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const liveSearch = async (query) => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/friends/search/${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      await axios.post(`${API}/friends/request`, 
        { to_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Request sent!', description: 'Friend request has been sent.' });
      setSearchResults(prev => prev.filter(u => u.id !== userId));
      setRecommendations(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send request', variant: 'destructive' });
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/request/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Friend added!', description: 'You are now friends.' });
      fetchFriends();
      fetchRequests();
      fetchRecommendations();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to accept request', variant: 'destructive' });
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/request/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequests();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject request', variant: 'destructive' });
    }
  };

  const removeFriend = async (friendId) => {
    try {
      await axios.delete(`${API}/friends/remove/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Friend removed', description: 'User has been removed from your friends list.' });
      fetchFriends();
      fetchRecommendations();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove friend', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="friends-list">
      {/* Search Users - Live */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Find Friends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="friend-search-input"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>

          {/* Live search dropdown */}
          {searchQuery.trim() && searchResults.length > 0 && (
            <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-y-auto" data-testid="search-results-dropdown">
              {searchResults.map((user) => (
                <div 
                  key={user.id} 
                  className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                  data-testid={`search-result-${user.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.profile_image} />
                      <AvatarFallback>{user.username[0].toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">{user.username}</span>
                  </div>
                  {user.is_friend ? (
                    <Badge variant="secondary" className="text-xs">Friends</Badge>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => sendFriendRequest(user.id)}
                      data-testid={`add-friend-${user.id}`}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {searchQuery.trim() && !searching && searchResults.length === 0 && (
            <p className="mt-2 text-sm text-gray-500 text-center py-2">No users found</p>
          )}
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-yellow-500" />
              Friend Requests
              <Badge variant="secondary">{requests.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {requests.map((request) => (
              <div 
                key={request.id} 
                className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                data-testid={`friend-request-${request.id}`}
              >
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>{request.from_username[0].toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{request.from_username}</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="default" 
                    onClick={() => acceptRequest(request.id)}
                    data-testid={`accept-request-${request.id}`}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => rejectRequest(request.id)}
                    data-testid={`reject-request-${request.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Friend Recommendations */}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Suggested For You
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {recommendations.slice(0, 6).map((rec) => (
                <div 
                  key={rec.id}
                  className="flex items-center justify-between p-3 bg-purple-50/50 rounded-lg border border-purple-100"
                  data-testid={`recommendation-${rec.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={rec.profile_image} />
                      <AvatarFallback className="bg-purple-200 text-purple-700">{rec.username[0].toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{rec.username}</p>
                      <p className="text-[10px] text-gray-500">{rec.reason}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    variant="outline"
                    className="shrink-0 ml-2"
                    onClick={() => sendFriendRequest(rec.id)}
                    data-testid={`add-rec-${rec.id}`}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Friends List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Your Friends
            <Badge variant="secondary">{friends.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {friends.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No friends yet. Search for users above!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {friends.map((friend) => (
                <div 
                  key={friend.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`friend-${friend.id}`}
                >
                  <div 
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => onSelectFriend && onSelectFriend(friend)}
                  >
                    <Avatar>
                      <AvatarImage src={friend.profile_image} />
                      <AvatarFallback style={{ backgroundColor: friend.banner_color }}>
                        {friend.username[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{friend.username}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => onSelectFriend && onSelectFriend(friend)}
                      data-testid={`view-stats-${friend.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => removeFriend(friend.id)}
                      data-testid={`remove-friend-${friend.id}`}
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FriendsList;
